/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { getCustomerShortResponse } from '@/account/types';
import { CustomerDataModelShort } from '../models/customer';
import { convertToCamelCase } from '@/account/lib/convertCase';
import { config } from '@/account/api';
import { merge } from '@adobe-commerce/elsie/lib';

export const transformCustomer = (
  response: getCustomerShortResponse
): CustomerDataModelShort => {
  const attributeList = response?.data?.customer?.custom_attributes
    ?.filter((el) => el)
    .reduce(
      (
        accumulator: Record<string, string | number | boolean>,
        currentValue
      ) => {
        accumulator[convertToCamelCase(currentValue.code as string)] =
          currentValue.value ?? '';
        return accumulator;
      },
      {} as Record<string, string | number | boolean>
    );

  const model = {
    email: response?.data?.customer?.email || '',
    firstName: response?.data?.customer?.firstname || '',
    lastName: response?.data?.customer?.lastname || '',
    middleName: response?.data?.customer?.middlename || '',
    gender: response?.data?.customer?.gender || '1',
    dateOfBirth: response?.data?.customer?.date_of_birth || '',
    prefix: response?.data?.customer?.prefix || '',
    suffix: response?.data?.customer?.suffix || '',
    createdAt: response?.data?.customer?.created_at || '',
    ...attributeList,
  };

  return merge(
    model, // default transformer
    config
      ?.getConfig()
      ?.models?.CustomerDataModelShort?.transformer?.(response.data) // custom transformer
  );
};
